import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import PerspectiveFlip from "@/components/PerspectiveFlip";

const CONFIDENCE = [
  { v: 25, label: "Guessing" },
  { v: 50, label: "Fairly sure" },
  { v: 75, label: "High confidence" },
  { v: 100, label: "Certain" },
];

export default function Assessment() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [section, setSection] = useState("cognitive");
  const [askConfidence, setAskConfidence] = useState(false);
  const [confidence, setConfidence] = useState(null);
  const [selected, setSelected] = useState(null);
  const [changed, setChanged] = useState(false);
  const [progress, setProgress] = useState(0);
  const [position, setPosition] = useState(1);
  const [total, setTotal] = useState(30);
  const [breakScreen, setBreakScreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [flipOpen, setFlipOpen] = useState(false);
  const [flipPending, setFlipPending] = useState(null); // {qid, prompt}
  const presentedAt = useRef(Date.now());

  const load = useCallback(async () => {
    setSelected(null); setChanged(false); setConfidence(null);
    try {
      const { data } = await api.get(`/assessment/${aid}/next`);
      if (data.done) { nav(`/results/${aid}`); return; }
      if (data.section_break) { setBreakScreen(true); setSection(data.next_section); return; }
      setQ(data.question);
      setSection(data.section);
      setAskConfidence(data.ask_confidence);
      setProgress(Math.round((data.progress || 0) * 100));
      setPosition(data.position); setTotal(data.total);
      presentedAt.current = Date.now();
    } catch (e) {
      if (e?.response?.status === 401) { nav("/auth"); return; }
      toast.error("Could not load next question");
    }
  }, [aid, nav]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (selected == null) { toast.error("Pick an option"); return; }
    if (askConfidence && confidence == null) { toast.error("Set your confidence"); return; }
    setLoading(true);
    try {
      await api.post("/assessment/answer", {
        assessment_id: aid,
        question_id: q.question_id,
        selected_index: selected,
        response_time_ms: Date.now() - presentedAt.current,
        confidence: askConfidence ? confidence : null,
        answer_changed: changed,
      });
      // Head vs Heart items -> trigger Perspective Flip modal before next load
      if (q.question_id.startsWith("hh_")) {
        setFlipPending({ qid: q.question_id, prompt: q.prompt });
        setFlipOpen(true);
      } else {
        await load();
      }
    } catch (e) { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const closeFlip = async () => {
    setFlipOpen(false); setFlipPending(null); await load();
  };

  const continueBreak = () => { setBreakScreen(false); load(); };

  const pick = (i) => { if (selected != null && selected !== i) setChanged(true); setSelected(i); };

  if (breakScreen) {
    return (
      <div><Nav />
        <div className="max-w-2xl mx-auto px-5 py-16 fade-up">
          <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">Section Complete</div>
          <h1 className="font-serif text-4xl sm:text-5xl mb-3">Nice work.</h1>
          <p className="text-slate-400 mb-6">You've finished the cognitive section. Next: scenarios about people, emotion and decisions. Take a breath.</p>
          <button onClick={continueBreak} className="px-6 py-3 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B]" data-testid="continue-break">
            Continue to emotional / social
          </button>
        </div>
      </div>
    );
  }

  if (!q) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400" data-testid="assessment-loading">Loading…</div></div>;

  return (
    <div className="min-h-screen"><Nav />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-6" data-testid="assessment-progress">
          <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest mb-2">
            <span className={section === "cognitive" ? "text-[#38BDF8]" : "text-[#F59E0B]"}>
              {section === "cognitive" ? "Cognitive" : "Emotional / Social"}
            </span>
            <span className="text-slate-500">{position} / {total} · ~{Math.max(1, Math.round((total - position) * 0.5))} min left</span>
          </div>
          <Progress value={progress} className="h-1.5 bg-slate-800" />
        </div>

        <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6 sm:p-8 fade-up" data-testid="question-card">
          <div className="uppercase font-mono text-xs tracking-widest text-slate-500 mb-4">
            {q.domain.replace(/_/g," ")} · difficulty {q.difficulty}/5
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl leading-snug mb-6" data-testid="question-prompt">{q.prompt}</h2>
          <div className="space-y-2">
            {q.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => pick(i)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border transition-colors ${selected===i?"border-[#E0A96D] bg-[#E0A96D]/10 text-white":"border-slate-700 hover:border-slate-500 text-slate-200"}`}
                data-testid={`option-${i}`}
              >
                <span className="font-mono text-xs text-slate-500 mr-3">{String.fromCharCode(65+i)}</span>
                {opt}
              </button>
            ))}
          </div>

          {askConfidence && (
            <div className="mt-6 pt-6 border-t border-slate-800" data-testid="confidence-block">
              <div className="uppercase font-mono text-xs tracking-widest text-[#A855F7] mb-2">Metacognitive check</div>
              <p className="text-sm text-slate-300 mb-3">How confident are you in your answer?</p>
              <div className="grid grid-cols-4 gap-2">
                {CONFIDENCE.map(c => (
                  <button key={c.v} onClick={()=>setConfidence(c.v)}
                    className={`px-2 py-2 rounded-lg border text-xs transition-colors ${confidence===c.v?"border-[#A855F7] bg-[#A855F7]/20 text-white":"border-slate-700 hover:border-slate-500 text-slate-300"}`}
                    data-testid={`confidence-${c.v}`}>
                    <div className="font-mono text-sm">{c.v}%</div>
                    <div className="text-[10px] text-slate-500">{c.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button onClick={submit} disabled={loading}
            className="mt-6 w-full py-3.5 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60 transition-colors"
            data-testid="submit-answer">
            {loading ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
      {flipPending && (
        <PerspectiveFlip
          open={flipOpen}
          onClose={closeFlip}
          assessmentId={aid}
          questionId={flipPending.qid}
          prompt={flipPending.prompt}
        />
      )}
    </div>
  );
}
